import {
  TAddOn,
  TAddOnRules,
  TCheckoutType,
  TPlan,
} from "@/components/Checkout/types.checkout";
import PlanCardUI from "@/components/Checkout/ui/PlanCardUI";
import PlanListCardUI from "@/components/Checkout/ui/PlanListCardUI";
import {
  createAddOnRuleMap,
  getAddOnByRules,
  getAddOnList,
  getAvailablePlansToAdd,
  getBundlePlans,
  getIndividualPlans,
  getOwnedAddOnQtyMap,
  TAddOnItem,
} from "@/components/Checkout/utils";
import { getEquivalentPlanIds } from "@/components/PlanMigration/handlers/getEquivalentPlanIds";
import { getBillingLabel } from "@/components/Pricing/v2/utils";
import {
  ADDON_REF_ID_MAPPING,
  ALL_INDIVIDUAL_PLANS,
  PLAN_REF_ID_MAPPING,
  PLANS_IN_BUNDLE,
} from "@/data/plans";
import { useNavigate } from "@/lib/router";
import useTracker from "@/tracking/useTracker";
import { TBillingInterval } from "@/types/app";
import { TProductRefId, TProductRefIdV1 } from "@/types/plan";
import { TPricingVersion } from "@/types/plan-data";
import {
  getIntervalPrice,
  getIntervalStrikePrice,
} from "@/utils/billingInterval";
import { useState } from "react";
import { BASE_DASHB_EVENT_PROPS, DASHBOARD_EVENTS } from "../events.dashboard";
import { TPlanActionType } from "../types.dashboard";
import { BillingBadge, ComponentHeader, ComponentLabel } from "../ui";
import { SwitchPlanInterval } from "../ui/SwitchPlanInterval";
import { getAddOnMaxCount, isAddOnForFounder } from "../utils";

const CONFIG = {
  upgradeTitle: "Available Upgrades",
  plansTitle: "Available plans",
};

// ── Types

type AvailableUpgradesProps = {
  planActionType: TPlanActionType;
  rawUserProductIds: TProductRefId[];
  rawUserProductIdV1?: TProductRefIdV1;
  planCards?: TPlan[];
  addOnCards: TAddOn[];
  addOnRules: TAddOnRules[];
  billingPeriod: TBillingInterval;
  ownedAddOnItemList: TAddOnItem[];
  isFounder: boolean;
};

type Strategy = {
  // Determines which plans are shown (upgrades only vs all available)
  effectiveActivePlanIds: TProductRefId[];
  // Determines where onPlanAdd / onAddOnAdd navigate
  checkoutType: TCheckoutType;
};

// ── Strategy
//
// All per-state branching lives here. To change behaviour for a state,
// change the strategy it resolves to.
//
//  isActiveState (active / cancelling)
//    plans  → upgrades only, filtered against user's current plan IDs
//    onAdd  → plan_manage checkout
//
//  isPlanOnTrial
//    plans  → all available (treated as having no active plans)
//    onAdd  → plan_purchase checkout
//
//  isPlanCancelled / default
//    plans  → all available
//    onAdd  → plan_purchase checkout
//
//  v1 user (any state above except trial)
//    onAdd  → plan_migration checkout (overrides all)

function resolveStrategy(params: {
  planActionType: TPlanActionType;
  activePlanIds: TProductRefId[];
  hasBundlePlan: boolean;
}): Strategy {
  const { planActionType, activePlanIds, hasBundlePlan } = params;

  const isActiveState =
    planActionType === "cancelling" || planActionType === "manage";

  const effectiveActivePlanIds: TProductRefId[] = (() => {
    if (!isActiveState) return [];
    if (hasBundlePlan)
      return [
        ...activePlanIds,
        ...(PLANS_IN_BUNDLE as readonly TProductRefId[]),
      ];
    return activePlanIds;
  })();

  switch (planActionType) {
    case "purchase":
      return { effectiveActivePlanIds, checkoutType: "plan_purchase" };
    case "migrate":
      return { effectiveActivePlanIds, checkoutType: "plan_migration" };
    case "cancelling":
    case "manage":
      return { effectiveActivePlanIds, checkoutType: "plan_manage" };
    default:
      return { effectiveActivePlanIds, checkoutType: "plan_purchase" };
  }
}

// ──  utilities

function buildCheckoutUrl(
  kind: "plan" | "addon",
  productRefId: string,
  strategy: Strategy,
  billingPeriod: TBillingInterval,
): string {
  const { checkoutType, effectiveActivePlanIds } = strategy;
  const params = new URLSearchParams({
    type: checkoutType,
    billing: billingPeriod,
  });

  switch (kind) {
    case "plan": {
      const combined = [...effectiveActivePlanIds, productRefId];
      const coversAll = ALL_INDIVIDUAL_PLANS.every((p) =>
        combined.includes(p as TProductRefId),
      );
      params.set("plan", coversAll ? PLAN_REF_ID_MAPPING.all : productRefId);
      break;
    }
    case "addon": {
      params.set("addon", productRefId);
      if (
        checkoutType !== "plan_migration" &&
        productRefId === ADDON_REF_ID_MAPPING.profiles
      ) {
        const alreadyHasEditing = effectiveActivePlanIds.includes(
          PLAN_REF_ID_MAPPING.editing as TProductRefId,
        );
        if (!alreadyHasEditing) params.set("plan", PLAN_REF_ID_MAPPING.editing);
      }
      break;
    }
  }

  if (checkoutType === "plan_migration") {
    params.set("cancellation", "true");
    params.set("pause", "true");
  }

  return `/checkout?${params}`;
}

function getBadges(plan: TPlan) {
  return {
    discount: {
      text: plan.pricing.savePercentage
        ? `Save ${plan.pricing.savePercentage}`
        : "",
    },
  };
}

function findPlan(planCards: TPlan[], id: string) {
  return planCards.find((p) => p.productRefId === id);
}

function findAddOn(addOnCards: TAddOn[], id: string) {
  return addOnCards.find((a) => a.productRefId === id);
}

function isBundlePlan(id: string) {
  return id.includes("bundle") || id.includes("ALL_APP");
}

// ── Component

export default function DashboardProductList({
  planActionType,
  planCards,
  addOnCards,
  addOnRules,
  billingPeriod,
  rawUserProductIds,
  rawUserProductIdV1,
  ownedAddOnItemList,
  isFounder,
}: AvailableUpgradesProps) {
  const navigate = useNavigate();
  const { track } = useTracker();

  const [billingToggle, setBillingToggle] = useState(billingPeriod);
  const [addOnQuantities, setAddOnQuantities] = useState<
    Record<string, number>
  >({});
  const getAddOnQuantity = (id: string) => addOnQuantities[id] ?? 1;
  const setAddOnQuantity = (id: string, qty: number) =>
    setAddOnQuantities((prev) => ({ ...prev, [id]: qty }));

  if (!planCards || planCards.length === 0) return null;

  // ── Derived flags from planActionType
  const isV1User = planActionType === "migrate";
  const hasNoActivePlan = planActionType === "purchase";
  const effectiveBillingPeriod = hasNoActivePlan
    ? billingToggle
    : billingPeriod;

  // ── V1 plan ID mapping
  const equivalentV2Ids: TProductRefId[] = isV1User
    ? getEquivalentPlanIds(rawUserProductIdV1!)
    : [];
  const activePlanIds = isV1User ? equivalentV2Ids : rawUserProductIds;
  const hasBundlePlan = activePlanIds.some(
    (id) => id.includes("ALL_APP") || id.includes("bundle"),
  );

  // ── Resolve strategy (what to show + where to navigate)
  const strategy = resolveStrategy({
    planActionType,
    activePlanIds,
    hasBundlePlan,
  });

  // ── Derive plan lists from strategy
  const availablePlansToAdd = getAvailablePlansToAdd(
    planCards,
    strategy.effectiveActivePlanIds,
  );

  // migrate = v1 active: only bundle upgrade; purchase state sees everything (fresh purchase)
  const bundlePlanFromAvailableList = getBundlePlans(availablePlansToAdd);
  const individualPlansFromAvailableList =
    planActionType === "migrate" ? [] : getIndividualPlans(availablePlansToAdd);

  // migrate = v1 active: only Profiles add-on if they have editing equivalent
  const v1HasEditing = equivalentV2Ids.includes(PLAN_REF_ID_MAPPING.editing);
  const addOnRulesMap = createAddOnRuleMap(addOnRules);
  const addOnAllowed =
    planActionType === "migrate"
      ? v1HasEditing
        ? [ADDON_REF_ID_MAPPING.profiles]
        : []
      : getAddOnByRules(rawUserProductIds, addOnRulesMap);
  const addOnList = getAddOnList(addOnAllowed, addOnCards);

  // ── Account for what the user already owns.
  // Each add-on can be owned up to its max_count. Subtract the owned quantity,
  // only offer the remaining, and drop add-ons the user has already maxed out.
  const ownedAddOnQtyMap = getOwnedAddOnQtyMap(ownedAddOnItemList);
  const availableAddOnList = addOnList
    // Founder-only add-ons are hidden from non-founder users.
    .filter((addOn) => !isAddOnForFounder(addOn) || isFounder)
    .map((addOn) => {
      const ownedQty = ownedAddOnQtyMap[addOn.productRefId] ?? 0;
      const remaining = getAddOnMaxCount(addOn) - ownedQty;
      return { addOn, remaining };
    })
    .filter(({ remaining }) => remaining > 0);

  // ── Handlers
  const pricingVersion: TPricingVersion = isV1User ? "v1" : "v2";

  const onPlanAdd = (
    productRefId: string,
    e: React.MouseEvent<HTMLElement>,
  ) => {
    navigate(
      buildCheckoutUrl("plan", productRefId, strategy, effectiveBillingPeriod),
    );
    const isBundle = isBundlePlan(productRefId);
    track(
      isBundle ? DASHBOARD_EVENTS.UPGRADE_PLAN : DASHBOARD_EVENTS.PLAN_ADD,
      {
        ...BASE_DASHB_EVENT_PROPS,
        plan_id: productRefId,
        plan_name: findPlan(planCards, productRefId)?.planName,
        billing_period: effectiveBillingPeriod,
        pricing_version: pricingVersion,
        current_plan_ids: rawUserProductIds,
        is_bundle: isBundle,
        cta_text: e.currentTarget.textContent,
      },
    );
  };

  const onAddOnAdd = (
    productRefId: string,
    _quantity: number,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    navigate(
      buildCheckoutUrl("addon", productRefId, strategy, effectiveBillingPeriod),
    );
    track(DASHBOARD_EVENTS.ADDON_ADD, {
      ...BASE_DASHB_EVENT_PROPS,
      addon_id: productRefId,
      addon_name: findAddOn(addOnCards, productRefId)?.name,
      billing_period: effectiveBillingPeriod,
      pricing_version: pricingVersion,
      current_plan_ids: rawUserProductIds,
      cta_text: e.currentTarget.textContent,
    });
  };

  const handleBillingToggleChange = (period: TBillingInterval) => {
    setBillingToggle(period);
    track(DASHBOARD_EVENTS.BILLING_TOGGLE_CHANGED, {
      ...BASE_DASHB_EVENT_PROPS,
      from: billingToggle,
      to: period,
      available_plans_count:
        individualPlansFromAvailableList.length +
        (bundlePlanFromAvailableList ? 1 : 0),
      available_addons_count: availableAddOnList.length,
    });
  };

  // ── Guards
  if (
    individualPlansFromAvailableList.length === 0 &&
    !bundlePlanFromAvailableList &&
    availableAddOnList.length === 0
  )
    return null;

  if (planActionType === "paused" || planActionType === "cancel") return null;

  // ── Render
  return (
    <div className="inline-flex w-full flex-col items-center justify-start gap-20 bg-[#FAF6F2] px-4 py-5 md:py-8 xl:px-0">
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-start gap-6">
        {/* header */}
        <div className="flex w-full flex-wrap items-center gap-4 sm:gap-6">
          <ComponentHeader>
            {hasNoActivePlan ? CONFIG.plansTitle : CONFIG.upgradeTitle}
          </ComponentHeader>

          {!hasNoActivePlan && <BillingBadge billingPeriod={billingPeriod} />}

          {hasNoActivePlan && (
            <SwitchPlanInterval
              currentPeriod={billingToggle}
              onToggle={handleBillingToggleChange}
            />
          )}
        </div>

        <div className="w-full grid-cols-2 flex-col items-start justify-start gap-4 md:grid lg:flex-row lg:gap-6">
          {/* Left Column — Plans */}
          {(bundlePlanFromAvailableList ||
            individualPlansFromAvailableList.length > 0) && (
            <div className="inline-flex w-full flex-1 flex-col items-start justify-start gap-4">
              {/* Bundled Plan */}
              {bundlePlanFromAvailableList && (
                <div className="flex w-full flex-col items-start gap-3 rounded-xl">
                  <ComponentLabel
                    text="Bundled"
                    color="brown"
                    subtext="All in one"
                    subtextColor="gray"
                  />
                  <PlanCardUI
                    id={bundlePlanFromAvailableList.productRefId}
                    name={bundlePlanFromAvailableList.planName}
                    type="plan"
                    planDescription={
                      bundlePlanFromAvailableList.planDescription
                    }
                    price={getIntervalPrice(
                      bundlePlanFromAvailableList.pricing,
                      effectiveBillingPeriod,
                    )}
                    listPrice={getIntervalStrikePrice(
                      bundlePlanFromAvailableList.pricing,
                      effectiveBillingPeriod,
                    )}
                    currency={bundlePlanFromAvailableList.pricing.currency}
                    interval={effectiveBillingPeriod}
                    cta={{
                      type: "upgrade" as const,
                      onClick: (productId, e) => onPlanAdd(productId, e),
                    }}
                    badges={getBadges(bundlePlanFromAvailableList)}
                    className="w-full"
                  />
                </div>
              )}

              {/* Individual Workflows */}
              {individualPlansFromAvailableList.length > 0 && (
                <div className="flex w-full flex-col items-start gap-2">
                  <ComponentLabel text="Individual workflows" color="gray" />
                  <div className="flex w-full flex-col gap-4 rounded-xl border border-edge-light bg-white p-4">
                    {individualPlansFromAvailableList.map((plan, index) => (
                      <div key={plan.id}>
                        <PlanCardUI
                          id={plan.productRefId}
                          name={plan.planName}
                          type="plan"
                          planDescription={plan.planDescription}
                          price={getIntervalPrice(
                            plan.pricing,
                            effectiveBillingPeriod,
                          )}
                          listPrice={getIntervalStrikePrice(
                            plan.pricing,
                            effectiveBillingPeriod,
                          )}
                          currency={plan.pricing.currency}
                          interval={effectiveBillingPeriod}
                          cta={{
                            type: "add" as const,
                            onClick: (productId, e) => onPlanAdd(productId, e),
                          }}
                          badges={getBadges(plan)}
                          className="border-none p-0"
                        />
                        {index <
                          individualPlansFromAvailableList.length - 1 && (
                          <hr className="my-4 border-t border-[#F6F6F6]" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Right Column — Add-ons */}
          {availableAddOnList.length > 0 && (
            <div className="flex w-full flex-1 flex-col items-start gap-3">
              <ComponentLabel text="Add ons" />
              <div className="flex w-full flex-col items-start gap-4">
                {availableAddOnList.map(({ addOn, remaining }) => {
                  const price = getIntervalPrice(
                    addOn.pricing,
                    effectiveBillingPeriod,
                  );
                  const strikePrice = getIntervalStrikePrice(
                    addOn.pricing,
                    effectiveBillingPeriod,
                  );

                  return (
                    <PlanListCardUI
                      key={addOn.id}
                      productRefId={addOn.productRefId}
                      name={addOn.name}
                      description={addOn.description}
                      featureList={addOn.featureList}
                      price={price}
                      strikePrice={strikePrice}
                      billingLabel={getBillingLabel(effectiveBillingPeriod)}
                      currency={addOn.pricing.currency}
                      quantity={getAddOnQuantity(addOn.productRefId)}
                      onQuantityChange={(qty) =>
                        setAddOnQuantity(addOn.productRefId, qty)
                      }
                      maxCount={remaining}
                      onAdd={(productId, quantity, e) =>
                        onAddOnAdd(productId, quantity, e)
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
